/**
 * Analytics utility — dual-fires to:
 *   1. gtag() → GA4 property G-TKN45F6L4M (direct, no GTM config needed)
 *   2. dataLayer → GTM-ND4WM5RC  (for GTM triggers / future tags)
 *
 * This means events appear in GA4 reports immediately after deploy,
 * AND in GTM for any additional tags you wire up later (ads, pixels, etc.).
 */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA4_ID = 'G-TKN45F6L4M';

/** Push to GTM dataLayer */
function pushDL(event: Record<string, unknown>) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

/** Send a GA4 event directly via gtag */
function gtagEvent(eventName: string, params: Record<string, unknown> = {}) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, { ...params, send_to: GA4_ID });
  }
}

/** Fire to both GA4 and GTM dataLayer */
function track(eventName: string, params: Record<string, unknown> = {}) {
  gtagEvent(eventName, params);
  pushDL({ event: eventName, ...params });
}

// ─── Page / Navigation ────────────────────────────────────────────────────────

/** Call on every SPA route change. GA4 config has send_page_view: false
 *  so this is the single source of truth for page_view hits. */
export function trackPageView(path: string, title: string) {
  gtagEvent('page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });
  pushDL({ event: 'page_view', page_path: path, page_title: title });
}

// ─── Affiliate / Revenue ──────────────────────────────────────────────────────

export type Broker = 'zerodha' | 'upstox' | 'angelone' | 'groww' | 'fyers';
export type AffiliatePlacement =
  | 'demat_hero'
  | 'demat_card'
  | 'demat_table'
  | 'demat_review'
  | 'demat_bottom_cta'
  | 'blog_bottom'
  | 'home_strip';

/** Fired whenever a user clicks any affiliate broker link. */
export function trackAffiliateClick(broker: Broker, placement: AffiliatePlacement) {
  track('affiliate_click', { broker, placement, currency: 'INR' });
}

// ─── Email / Lead Capture ─────────────────────────────────────────────────────

/** Fired when subscribe form is submitted (before API call). */
export function trackSubscribeAttempt(source: string) {
  track('subscribe_attempt', { form_location: source });
}

/** Fired on successful Brevo API response (201 / 204). */
export function trackSubscribeSuccess(source: string) {
  track('subscribe_success', { form_location: source });
  // Also send as GA4 recommended 'generate_lead' event
  gtagEvent('generate_lead', { form_location: source, send_to: GA4_ID });
}

// ─── GPT Tool CTA ─────────────────────────────────────────────────────────────

/** Fired when a user clicks any link to the ISMGPT custom GPT on chatgpt.com.
 *  Sent for every such link by GptClickTracker (delegated listener), so new
 *  CTAs are tracked without extra wiring. */
export function trackGptCTAClick(placement: string, extra: Record<string, unknown> = {}) {
  track('gpt_cta_click', { placement, ...extra });
}

// ─── Ask / Query ──────────────────────────────────────────────────────────────

/** Fired when user submits a stock query on /ask. */
export function trackQuerySubmit(ticker: string) {
  track('query_submit', {
    ticker: ticker.toUpperCase(),
    // GA4 search event mapping
    search_term: ticker.toUpperCase(),
  });
}

/** Fired when results page loads. */
export function trackQueryResult(ticker: string, success: boolean) {
  track('query_result', { ticker: ticker.toUpperCase(), success });
}

// ─── Engagement ───────────────────────────────────────────────────────────────

/** Fired when user views the demat comparison table. */
export function trackDematTableView() {
  track('demat_table_view', {});
}

/** Fired when a blog article is opened. */
export function trackBlogOpen(slug: string, title: string) {
  track('blog_open', { article_slug: slug, article_title: title });
}
